import React, { useEffect, useMemo, useRef, useState } from 'react';
import { sumLines } from '../lib/payoutParser';
import { sanitizePlain } from '../utils/numOcr';
import { ocrImageToTotal } from '../utils/ocrImageToTotal';

export default function PayoutModal({ open, initialText, onClose, onConfirm }) {
  const [localText, setLocalText] = useState(() => sanitizePlain(initialText || ''));
  const [isOcrLoading, setIsOcrLoading] = useState(false);
  const [ocrConfidence, setOcrConfidence] = useState(null);
  const [ocrError, setOcrError] = useState('');
  const fileInputRef = useRef(null);

  // openのたびに初期化
  useEffect(() => {
    if (open) {
      setLocalText(sanitizePlain(initialText || ''));
      setOcrConfidence(null);
      setOcrError('');
      setIsOcrLoading(false);
    }
  }, [open, initialText]);

  const { sum, details } = useMemo(() => sumLines(localText), [localText]);
  const invalidCount = useMemo(
    () => details.filter((d) => d.raw.trim() && d.value == null).length,
    [details]
  );

  if (!open) return null;

  const handleSelectImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setIsOcrLoading(true);
    setOcrError('');
    setOcrConfidence(null);
    try {
      const { sanitizedText, confidence } = await ocrImageToTotal(file);
      setLocalText(sanitizedText);
      setOcrConfidence(confidence);
    } catch (err) {
      console.error(err);
      setOcrError('画像の取り込みに失敗しました。もう一度お試しください。');
    } finally {
      setIsOcrLoading(false);
      if (event.target) event.target.value = '';
    }
  };

  const lowConfidence = !isOcrLoading && ocrConfidence != null && Number(ocrConfidence) < 60;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 背景オーバーレイ */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* モーダル本体 */}
      <div className="relative z-10 w-[min(720px,92vw)] max-h-[80vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <header className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">獲得出玉 内訳入力</h2>
          <button
            className="px-3 py-1 rounded-lg border hover:bg-gray-50"
            onClick={onClose}
            aria-label="閉じる"
          >
            ×
          </button>
        </header>

        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="text-sm text-gray-600">
            1行につき1項目（例：<code>1200</code>、<code>1,200</code>、<code>500x3</code>）。
            <span className="text-gray-500 ml-1">
              ※ <code>{'//'}</code> や <code>{'#'}</code> 以降はコメント扱い
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <button
                className="bg-blue-100 hover:bg-blue-200 text-blue-800 font-semibold py-1.5 px-4 rounded"
                onClick={handleSelectImage}
                type="button"
                disabled={isOcrLoading}
              >
                {isOcrLoading ? '処理中…' : '画像取り込み'}
              </button>
              {!isOcrLoading && ocrConfidence != null && (
                <span className="text-xs text-gray-600">
                  解析信頼度: {Math.round(Number(ocrConfidence))}%
                </span>
              )}
            </div>
            {lowConfidence && (
              <div className="text-xs text-orange-700">
                認識結果の信頼度が低いため、内容を確認してください。
              </div>
            )}
            {ocrError && <div className="text-xs text-red-700">{ocrError}</div>}
          </div>

          <textarea
            value={localText}
            onChange={(e) => setLocalText(sanitizePlain(e.target.value))}
            placeholder={'1200\n500x3 # チャンス中など\n800'}
            className="w-full h-48 p-3 rounded-xl border focus:outline-none focus:ring"
          />

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-3 rounded-xl border bg-gray-50">
              <div className="text-sm text-gray-500">計算結果（合計）</div>
              <div className="text-2xl font-bold">{sum}</div>
              {invalidCount > 0 && (
                <div className="mt-1 text-xs text-orange-600">無視された行: {invalidCount}</div>
              )}
            </div>

            <div className="p-3 rounded-xl border overflow-y-auto max-h-40">
              <div className="text-sm font-medium mb-1">内訳プレビュー</div>
              <ul className="text-sm space-y-1">
                {details.map((d) => (
                  <li
                    key={d.index}
                    className={
                      'flex items-center justify-between gap-2 ' +
                      (d.value == null && d.raw.trim() ? 'text-orange-700' : '')
                    }
                  >
                    <span className="truncate text-gray-700">
                      {d.raw || <span className="text-gray-400">(空行)</span>}
                    </span>
                    <span className="font-mono">{d.value == null ? '-' : d.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <footer className="flex items-center justify-end gap-2 p-4 border-t">
          <button
            className="px-4 py-2 rounded-xl border hover:bg-gray-50"
            onClick={() => setLocalText('')}
          >
            クリア
          </button>
          <button
            className="px-4 py-2 rounded-xl bg-blue-700 text-white hover:opacity-90"
            onClick={() => onConfirm({ text: localText, sum })}
          >
            合計 {sum} を反映
          </button>
        </footer>
      </div>
    </div>
  );
}
